import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import AmountField from "@/components/AmountField";
import { useRaisableReasons, useRaiseAdjustment } from "@/hooks/useAdjustments";
import { useGroups } from "@/hooks/useGroups";
import { useOrganizations } from "@/hooks/useOrganizations";
import type { DrawOrder, RaisableReason } from "@/interfaces/adjustments";
import type { GroupNode } from "@/interfaces/group";
import type { Organization } from "@/interfaces/organization";

/**
 * Raise a credit adjustment for review.
 *
 * Nothing here moves credit. Submitting records the request and puts it in the approval queue; a
 * second person has to approve before anything happens, which is why the confirm button says
 * "Submit for approval" rather than anything that sounds final.
 *
 * The maker types a POSITIVE number of credits and the reason decides the sign — a picker that
 * offers "clawback" and then accepts +500 would be inviting the mistake.
 */
export default function NewAdjustmentDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const raise = useRaiseAdjustment();
  const { data: reasons = [] } = useRaisableReasons();

  const [org, setOrg] = useState<Organization | null>(null);
  const [orgSearch, setOrgSearch] = useState("");
  const [group, setGroup] = useState<GroupNode | null>(null);
  const [reasonCode, setReasonCode] = useState("");
  const [credits, setCredits] = useState("");
  const [drawOrder, setDrawOrder] = useState<DrawOrder>("lifo");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: orgsResp } = useOrganizations({ search: orgSearch || undefined, limit: 20 });
  const orgs = orgsResp?.data ?? [];
  const groups = useGroups(org?.id ?? 0).data ?? [];

  /**
   * Preselect the ROOT group once an org is chosen.
   *
   * On a Starter or Pro plan the root is the only group there is, so asking the maker to pick it is
   * a required click with one legal answer. It stays a real, editable field for the organisations
   * that do have a tree — this only fills in the answer that is right in almost every case.
   */
  useEffect(() => {
    if (!org || group) return;
    const root = groups.find((g) => g.parentId === null) ?? groups[0];
    if (root) setGroup(root);
  }, [org, group, groups]);

  const reason: RaisableReason | undefined = reasons.find((r) => r.code === reasonCode);
  const isRemoval = reason?.direction === "remove";

  function reset() {
    setOrg(null);
    setOrgSearch("");
    setGroup(null);
    setReasonCode("");
    setCredits("");
    setDrawOrder("lifo");
    setNote("");
    setError(null);
  }
  function handleClose() {
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    if (!group) return setError("Pick the group the adjustment applies to");
    if (!reason) return setError("Pick a reason");

    const n = Number(credits);
    if (!Number.isFinite(n) || n <= 0) return setError("Enter a positive number of credits");
    if (!note.trim()) return setError("A note is required — it is what the approver reads");

    try {
      await raise.mutateAsync({
        reasonCode: reason.code,
        targetType: "group",
        targetId: group.id,
        // The reason carries the direction; the maker never types a minus sign.
        amount: String(isRemoval ? -n : n),
        ccy: "usd",
        note: note.trim(),
        ...(isRemoval ? { drawOrder } : {}),
      });
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not raise the adjustment");
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: "var(--font-lato)", fontWeight: 700 }}>
        New credit adjustment
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
          This does not move credit. It creates a request that another approver has to approve
          before anything changes.
        </Typography>

        <Autocomplete
          options={orgs}
          value={org}
          getOptionLabel={(o) => o.name ?? ""}
          onInputChange={(_, v) => setOrgSearch(v)}
          onChange={(_, v) => {
            setOrg(v);
            setGroup(null);
          }}
          renderInput={(p) => <TextField {...p} label="Organization" size="small" />}
        />

        <Autocomplete
          options={groups}
          value={group}
          disabled={!org}
          getOptionLabel={(g) => g.name ?? ""}
          onChange={(_, v) => setGroup(v)}
          renderInput={(p) => (
            <TextField
              {...p}
              label="Group"
              size="small"
              helperText={
                org && groups.length === 1
                  ? "This client has one wallet, already selected."
                  : org && groups.length > 1
                    ? "Defaulted to the top-level group — change it if the credits belong elsewhere."
                    : undefined
              }
            />
          )}
        />

        <TextField
          select
          label="Reason"
          size="small"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          {reasons.map((r) => (
            <MenuItem key={r.code} value={r.code}>
              {r.label ?? r.code} — {r.direction === "add" ? "adds credit" : "removes credit"}
            </MenuItem>
          ))}
        </TextField>
        {reason?.description ? (
          <Typography variant="caption" sx={{ color: "var(--color-graphite)", mt: -1.5 }}>
            {reason.description}
          </Typography>
        ) : null}

        {/* Credits read the way every other figure on the platform does — 1,234.50. */}
        <AmountField
          label="Credits"
          value={credits}
          onChange={setCredits}
          helperText={
            reason
              ? isRemoval
                ? "Credits to REMOVE from the group"
                : "Credits to ADD to the group"
              : "Pick a reason first — it decides whether this adds or removes"
          }
        />

        {/* Only a removal draws from existing stock, so only a removal has an order to choose. */}
        {isRemoval ? (
          <TextField
            select
            label="Take credits from"
            size="small"
            value={drawOrder}
            onChange={(e) => setDrawOrder(e.target.value as DrawOrder)}
            helperText="Newest first is the usual choice — a clawback normally takes back what was most recently granted."
          >
            <MenuItem value="lifo">Newest credits first (LIFO)</MenuItem>
            <MenuItem value="fifo">Oldest credits first (FIFO)</MenuItem>
          </TextField>
        ) : null}

        <TextField
          label="Note"
          size="small"
          multiline
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          helperText="What happened, and why this is the right correction. The approver reads this."
        />

        {error ? (
          <Typography variant="body2" sx={{ color: "var(--color-error, #b3261e)" }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={raise.isPending}>
          {raise.isPending ? "Submitting…" : "Submit for approval"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
